import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { Channel } from 'channelx';
import { Channels } from '@/app.manager/events-manager/Channels';
import { authService } from './authService';

const errorsChannel = Channel.use(Channels.errorsChannel);

function preventCachedResponse(httpClient: AxiosInstance) {
  httpClient.interceptors.request.use(config => {
    if (config.method === 'get' && !config.url?.includes('s3.amazonaws.com')) {
      config.params = {
        t: new Date().getTime(),
        ...config.params,
      };
    }

    return config;
  });
}

function setRequestHandling(httpClient: AxiosInstance) {
  preventCachedResponse(httpClient);
}

function setErrorHandling(httpClient: AxiosInstance, silent = false) {
  httpClient.interceptors.response.use(
    config => {
      errorsChannel.publish({ type: 'Network::restored' });

      return config;
    },
    error => {
      const { status, data } = error?.response || {};
      const { message, description, severity, customMessage } = data || {};

      errorsChannel.publish({
        type: 'Network::error',
        payload: { error, status, title: message, description, severity, silent, customMessage },
      });

      if (status >= 400) {
        errorsChannel.publish({
          type: 'http::error',
          payload: {
            status,
            silent,
          },
        });
      }

      return Promise.reject(error);
    },
  );
}

function setHeaders(config: AxiosRequestConfig) {
  // TODO: DGC-36248 ignore if using Controlled Mode / Cookie
  if (!authService.getAuthToken()) {
    return config;
  }

  if (!config.headers) {
    config.headers = {};
  }
  config.headers.Authorization = `Bearer ${authService.getAuthToken()}`;

  return config;
}

export class HttpClientFactory {
  constructor() {
    HttpClientFactory.buildDefaultClient();
  }

  private static _baseClient: AxiosInstance;

  static get baseClient(): AxiosInstance {
    if (HttpClientFactory._baseClient) {
      return HttpClientFactory._baseClient;
    }

    HttpClientFactory.buildDefaultClient();

    return HttpClientFactory._baseClient;
  }

  private static _baseClientSilent: AxiosInstance;

  static build(config: any = {}, options: any = {}): AxiosInstance {
    const httpClient: AxiosInstance = axios.create(config);

    const { silentErrorHandling } = options;

    setRequestHandling(httpClient);
    setErrorHandling(httpClient, silentErrorHandling === true);

    httpClient.interceptors.request.use(setHeaders);

    return httpClient;
  }

  static get baseClientSilent(): AxiosInstance {
    if (HttpClientFactory._baseClientSilent) {
      return HttpClientFactory._baseClientSilent;
    }

    HttpClientFactory.buildDefaultClient();

    return HttpClientFactory._baseClientSilent;
  }

  static buildDefaultClient() {
    HttpClientFactory._baseClient = HttpClientFactory.build();
    HttpClientFactory._baseClientSilent = HttpClientFactory.build({}, { silentErrorHandling: true });
  }

  static construct() {
    return new HttpClientFactory();
  }
}
